<script lang="ts">
    import { getContext, onDestroy, tick } from 'svelte'
    import { fade } from 'svelte/transition'

    import { gettext as _ } from 'hydria/services/gettext'
    import { simpleFetch } from 'hydria/services/session'
    import { algorithmIndex, algorithms, digestIndex, digests, ready as dnssecDataReady } from 'wdom/services/dnssec'
    import * as yup from 'yup'

    import { wbr } from 'hydria/utils/strings'

    import type { DomainHandler } from './domain'
    import type { IDomain } from 'wdom/domain/interfaces'
    import { isEqual } from 'lodash-es'
    import { notify } from 'hydria/services/notifications'
    import Icon from '@iconify/svelte'
    import { asAny } from 'hydria/svelte_helpers'

    interface Props {
        preImport?: boolean
        close: () => void
    }

    let { preImport = false, close }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface IDSRecordEdit {
        key_tag?: number
        alg?: number
        digest_type?: number
        digest?: string
        op?: 'new' | 'remove'
    }

    // const domainId = <Readable<string>>getContext('domainId')
    const Domain = getContext('Domain') as DomainHandler

    let status = $state()
    unsubs.push(
        Domain.status.subscribe((nstatus) => {
            if (nstatus == 'ready')
                tick().then(() => {
                    load()
                })
            status = nstatus
        }),
    )

    let domain: IDomain = $derived(status == 'ready' ? Domain.domain : <IDomain>{})

    let dsTaskRunning = $state(false)
    unsubs.push(
        Domain.runningTasks.dsRecords.subscribe(($running) => {
            dsTaskRunning = $running
            if (dsTaskRunning) close()
        }),
    )

    let dsRecords: IDSRecordEdit[] = $state([])

    function load() {
        const recs: IDSRecordEdit[] = []
        for (const dsr of preImport ? Domain.zoneDomainRelation.ds_records : domain.ds_records) {
            recs.push({ ...dsr })
        }
        dsRecords = recs
    }

    let form: IDSRecordEdit = $state({})

    function reset() {
        form = {}
    }

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, form: IDSRecordEdit) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    const schema = yup.object().shape({
        key_tag: yup.number().min(1).max(65535).required(),
        alg: yup
            .number()
            .required()
            .test('validAlg', '${path} is not a valid algorithm', (value) => ($algorithmIndex[value] != null && $algorithmIndex[value].allowed === true && $algorithmIndex[value][domain.system] === true ? true : false)),
        digest_type: yup
            .number()
            .required()
            .test('validDig', '${path} is not a valid digest', (value) => ($digestIndex[value] != null && $digestIndex[value].allowed === true && $digestIndex[value][domain.system] === true ? true : false)),
        digest: yup
            .string()
            .required()
            .lowercase()
            .trim()
            .matches(/^[0-9a-fa-f\s]+$/)
            .when('digest_type', ([digest_type], schema: any) => {
                if ($digestIndex[digest_type] == null || $digestIndex[digest_type].allowed !== true || $digestIndex[digest_type][domain.system] !== true) return schema

                return schema.min($digestIndex[digest_type].length).max($digestIndex[digest_type].length)
            }),
    })
    let formIsValid = $derived(schema.isValidSync(form))
    let key_tagIsValid = $derived(validateSyncAt('key_tag', form))
    let algIsValid = $derived(validateSyncAt('alg', form))
    let digest_typeIsValid = $derived(validateSyncAt('digest_type', form))
    let digestIsValid = $derived(validateSyncAt('digest', form))

    let saving = $state(false)
    let modified = $derived(!isEqual(dsRecords, domain.ds_records))

    function toggleRemove(dsr: IDSRecordEdit) {
        switch (dsr.op) {
            case 'new':
                const i = dsRecords.indexOf(dsr)
                if (i >= 0) {
                    dsRecords.splice(i, 1)
                }
                break
            case 'remove':
                delete dsr.op
                break
            default:
                dsr.op = 'remove'
        }
        // dsRecords = dsRecords
    }

    function addDsr() {
        dsRecords.push({ ...form, op: 'new' })
        reset()
        // dsRecords = dsRecords
    }

    async function save() {
        saving = true
        const recs: IDSRecordEdit[] = []
        for (const rec of dsRecords) {
            if (rec.op === 'remove') continue
            if (rec.op != null) delete rec.op
            recs.push(rec)
        }

        try {
            const r = await simpleFetch('POST', `/json/domain/1/${domain.idna}/ds-records`, { records: recs })
            if (r.status == 202) {
                close()
                return
            }
        } catch (e) {}
        notify({
            type: 'danger',
            title: $_('DS Records'),
            text: $_('There was an error processing the new DS records for the domain.'),
        })
        saving = false
    }

    $effect(() => {
        if (status == 'ready' && $dnssecDataReady && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!(status == 'ready' && $dnssecDataReady) && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })
</script>

<style>
    .is-remove td {
        color: rgba(var(--bs-danger-rgb), 1);
    }
    .is-new td {
        color: rgba(var(--bs-success-rgb), 1);
    }
</style>

{#if status == 'ready' && $dnssecDataReady}
    <div class="modal modal-ds-records" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>DNSSEC</div>
                </div>

                <div class="modal-body p-0">
                    <div class="table-responsive modal-ds-record-table ds-record-list mb-0">
                        <table class="table table-striped fixed-no-overflow table-condensed ds-record-table">
                            <thead>
                                <tr>
                                    <th class="key-tag">Key tag</th>
                                    <th class="alg">Algorithm</th>
                                    <th class="digest-type">Digest type</th>
                                    <th class="digest">Digest</th>
                                    <th class="action"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {#if !dsRecords.length}
                                    <tr><th colspan="5">{$_('No DS records')}</th></tr>
                                {:else}
                                    {#each dsRecords as dsr}
                                        <tr class:is-new={dsr.op === 'new'} class:is-remove={dsr.op === 'remove'}>
                                            <td class="key-tag">{dsr.key_tag}</td>
                                            <td class="alg">{dsr.alg} – {$algorithmIndex[asAny(dsr).alg].text}</td>
                                            <td class="digest-type">{dsr.digest_type} – {$digestIndex[asAny(dsr).digest_type].name}</td>
                                            <td class="digest">{@html wbr(asAny(dsr).digest, [32, 20])}</td>
                                            <td class="action">
                                                <button type="button" class="btn btn-sm" onclick={() => toggleRemove(dsr)} class:btn-outline-secondary={dsr.op === 'new'} class:btn-warning={dsr.op !== 'new'} class:active={dsr.op === 'remove'}><Icon inline icon="fa:trash-o" /></button>
                                            </td>
                                        </tr>
                                    {/each}
                                {/if}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="modal-body">
                    <form
                        novalidate
                        class="d-grid gap-2"
                        onsubmit={(e) => {
                            e.preventDefault()
                            addDsr()
                        }}
                    >
                        <div class="row">
                            <label for="dsrEditModalKeyTag" class="col-sm-3 col-form-label text-end">Key tag</label>
                            <div class="col-sm-9">
                                <input type="number" class="form-control" id="dsrEditModalKeyTag" placeholder="1 … 65535" bind:value={form.key_tag} class:is-invalid={!key_tagIsValid} />
                            </div>
                        </div>
                        <div class="row">
                            <label for="dsrEditModalAlg" class="col-sm-3 col-form-label text-end">Algorithm</label>
                            <div class="col-sm-9">
                                <select class="form-control" id="dsrEditModalAlg" bind:value={form.alg} class:is-invalid={!algIsValid}>
                                    {#if form.alg == null}
                                        <option value={null}>{$_('Select an algorithm')}</option>
                                    {/if}
                                    {#each $algorithms as alg (alg.id)}
                                        {#if alg.allowed === true && alg[domain.system] === true}
                                            <option value={alg.id}>{alg.id} – {alg.text}</option>
                                        {/if}
                                    {/each}
                                </select>
                            </div>
                        </div>
                        <div class="row">
                            <label for="dsrEditModalDigestType" class="col-sm-3 col-form-label text-end">Digest type</label>
                            <div class="col-sm-9">
                                <select class="form-control" id="dsrEditModalDigestType" bind:value={form.digest_type} class:is-invalid={!digest_typeIsValid}>
                                    {#if form.alg == null}
                                        <option value={null}>{$_('Select a digest type')}</option>
                                    {/if}
                                    {#each $digests as dig (dig.id)}
                                        {#if dig.allowed === true && dig[domain.system] === true}
                                            <option value={dig.id}>{dig.id} – {dig.name}</option>
                                        {/if}
                                    {/each}
                                </select>
                            </div>
                        </div>
                        <div class="row">
                            <label for="dsrEditModalDigest" class="col-sm-3 col-form-label text-end">Digest</label>
                            <div class="col-sm-9">
                                <textarea id="dsrEditModalDigest" class="form-control" class:is-invalid={!digestIsValid} bind:value={form.digest}></textarea>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-9 offset-sm-3">
                                <button class="btn btn-primary" type="submit" disabled={saving || !formIsValid}>{$_('Add to list')}</button>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" disabled={saving} onclick={() => close()}>{$_('Cancel')}</button>
                    <button class="btn btn-warning" type="button" disabled={saving || !modified} onclick={() => save()}>
                        {#if saving}
                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {/if}
                        {$_('Save')}
                    </button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                if (!saving) close()
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}
